import Button, { ButtonType } from "@/components/reusable/Button";
import Link from "@/components/ui/Link";
import { getBlogCategoryUrl, getBlogPostUrl } from "@/lib/blog";
import { getReadableImageUrl } from "@/lib/directus";
import Image from "next/image";
import { useRouter } from "next/router";
import { useEffect, useState } from "react";
import BlogTabs from "../BlogTabs/BlogTabs";
import { htmlToPlainText, createExcerpt } from "../utils";
import Pagination from "./Pagination";
import BreadCrumbs from "@/components/Gallery/BreadCrumbs";

export interface BlogPost {
  id: string;
  title: string;
  slug: string;
  content: string;
  published_at: string;
  author: {
    slug: string;
    title: string;
  };
  featured_image?: {
    id: string;
    filename_disk?: string;
    filename_download: string;
  };
  categories?: {
    blog_categories_id: {
      slug: string;
      title: string;
    };
  }[];
}

export interface BlogPostsListData {
  posts: BlogPost[];
  total: number;
  totalPages: number;
  currentPage: number;
  categories: {
    id: string;
    slug: string;
    title: string;
  }[];
  archives: {
    date: string;
    count: number;
  }[];
  categorySlug?: string;
  left_column_data?: any;
}

interface BlogPostsListProps {
  data: BlogPostsListData;
  paginationBaseUrl: string;
  showEmptyState?: boolean;
}

const formatDate = (dateString: string) => {
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      timeZone: "UTC",
    });
  } catch (e) {
    return dateString;
  }
};

export default function BlogPostsList({
  data,
  paginationBaseUrl,
  showEmptyState = true,
}: BlogPostsListProps) {
  const [mounted, setMounted] = useState(false);
  const [displayedPosts, setDisplayedPosts] = useState(data.posts);
  const router = useRouter();

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    // Sort posts based on URL sort parameter
    const sortOrder = (router.query.sort as string) || "newest";
    const sortedPosts = [...data.posts].sort((a, b) => {
      const dateA = new Date(a.published_at).getTime();
      const dateB = new Date(b.published_at).getTime();
      return sortOrder === "newest" ? dateB - dateA : dateA - dateB;
    });
    setDisplayedPosts(sortedPosts);
  }, [router.query.sort, data.posts]);

  // Transform categories data to match BlogTabs expected format
  // Note: Empty categories are already filtered by the service layer
  const transformedCategories =
    data.categories?.map((category) => ({
      id: category.id,
      slug: category.slug,
      title: category.title,
    })) || [];

  if (!data.posts.length && showEmptyState) {
    return (
      <div className="text-center py-16">
        <p className="text-neutral-600">No posts found for this category.</p>
      </div>
    );
  }

  const selectedCategory = transformedCategories.find(
    (category) => category.slug === data.categorySlug
  );

  return (
    <>
      <BreadCrumbs
        className="bg-accent-color-three-dark text-[#fff] py-6 px-6 md:py-6 md:px-12 lg:py-8 lg:px-32 m-0 text-span-medium"
        segments={
          selectedCategory
            ? [
                {
                  title: selectedCategory.title,
                  url: getBlogCategoryUrl(selectedCategory.slug),
                },
              ]
            : []
        }
      />
      <section
        id="blog-index"
        className="bg-accent-color-three py-12 px-6 md:px-12 md:py-16 desktop:py-36 desktop:px-36"
      >
        <div className="container mx-auto max-w-[1400px]">
          <div className="flex flex-col gap-12 desktop:gap-20">
            <div className="BlogNav max-md:justify-center max-md:flex">
              <div className="flex justify-between items-center">
                <BlogTabs
                  data={{
                    categories: transformedCategories,
                    archives: data.archives || [],
                  }}
                />
                {/* <BlogOrder /> */}
              </div>
            </div>

            <div>
              <div className="grid grid-cols-1 md:grid-cols-2 desktop:grid-cols-3 gap-8">
                {displayedPosts.map((post) => (
                  <article
                    key={post.id}
                    className="overflow-hidden flex flex-col h-full gap-6"
                  >
                    <Link href={getBlogPostUrl(post.slug)} className="block">
                      <div className="relative aspect-[16/10] w-full">
                        <Image
                          src={
                            post.featured_image
                              ? getReadableImageUrl(
                                  post.featured_image.id,
                                  post.featured_image.filename_download
                                )
                              : "/favicon.svg"
                          }
                          alt={htmlToPlainText(post.title) || ""}
                          fill
                          className="object-cover"
                          sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 33vw"
                          quality={100}
                          loading={"lazy"}
                        />
                      </div>
                    </Link>

                    <div className="flex flex-col gap-3 h-full">
                      <header className="text-secondary-light">
                        <Link
                          href={getBlogPostUrl(post.slug)}
                          className="hover:text-brown font-serif text-blog-h2-mobile md:text-blog-h2-tablet desktop:text-blog-h2-desktop"
                        >
                          <h2 className="hover:text-brown font-serif text-blog-h2-mobile md:text-blog-h2-tablet desktop:text-blog-h2-desktop">
                            {htmlToPlainText(post.title)}
                          </h2>
                        </Link>
                      </header>
                      <span className="text-accent-color-two-light text-span-medium">
                        {formatDate(post.published_at)}
                      </span>
                      <p className="line-clamp-4 text-white text-paragraph-medium h-full">
                        {createExcerpt(post.content, 200)}
                      </p>
                    </div>

                    <footer>
                      <Button
                        type={ButtonType.primary}
                        link={getBlogPostUrl(post.slug)}
                      >
                        Read Full Article
                      </Button>
                    </footer>
                  </article>
                ))}
              </div>
            </div>
            <Pagination
              currentPage={data.currentPage || 1}
              totalPages={data.totalPages}
              baseUrl={paginationBaseUrl}
            />
          </div>
        </div>
      </section>
    </>
  );
}
